import { Injectable, inject } from '@angular/core';
import {
  combineLatest,
  defer,
  map,
  of,
  retry,
  shareReplay,
  startWith,
  catchError,
  distinctUntilChanged,
} from 'rxjs';
import { LoggerService, StorageService } from '@victory-lib-ui/shared/utils';
import { User } from '@victory-lib-ui/shared/models';
import {
  AiInsight,
  DashboardOverviewResponse,
  DashboardRecentPassagesResponse,
  DashboardState,
  DashboardStatsResponse,
  DashboardVerseOfDayResponse,
  DashboardViewModel,
  ReadingChartData,
  ReadingProgress,
  ReadingStreak,
  RecentActivity,
  StatCard,
  StatCardView,
  StudyStats,
  ThemeProgress,
  DashboardSubscription,
} from './dashboard.models';
import { DashboardApiService } from './dashboard-api.service';

const DEFAULT_CHART_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RETRY_COUNT = 2;

const DEFAULT_SUBSCRIPTION: DashboardSubscription = {
  plan_name: 'Premium',
  status: 'Active',
  renews_on: '',
  cta_label: 'Manage Plan',
};

const DEFAULT_READING_STREAK: ReadingStreak = {
  current_days: 0,
  longest_days: 0,
  last_read_at: null,
};

const DEFAULT_STUDY_STATS: StudyStats = {
  total_sessions: 0,
  total_minutes: 0,
  sessions_this_week: 0,
  average_session_minutes: 0,
};

const DEFAULT_THEME_PROGRESS: ThemeProgress = {
  theme: 'Faith in Action',
  progress_percent: 0,
  completed: 0,
  total: 0,
};

const DEFAULT_AI_INSIGHT: AiInsight = {
  title: 'Daily Insight',
  summary: 'Start reading to see your personalized insight.',
  reference: '—',
};

const DEFAULT_OVERVIEW: DashboardOverviewResponse = {
  subscription: DEFAULT_SUBSCRIPTION,
  quick_access: [],
  continue_reading: [],
  reading_streak: DEFAULT_READING_STREAK,
  study_stats: DEFAULT_STUDY_STATS,
  theme_progress: DEFAULT_THEME_PROGRESS,
  ai_insight: DEFAULT_AI_INSIGHT,
};

const DEFAULT_PROGRESS: ReadingProgress = {
  overall: 0,
  old_testament: 0,
  new_testament: 0,
};

const DEFAULT_STATS: DashboardStatsResponse = {
  stat_cards: [],
  progress: DEFAULT_PROGRESS,
  chart: {
    labels: DEFAULT_CHART_LABELS,
    values: DEFAULT_CHART_LABELS.map(() => 0),
  },
};

const DEFAULT_RECENT_ACTIVITY: RecentActivity = {
  bookmarks: [],
  notes: [],
  highlights: [],
};

const DEFAULT_RECENT: DashboardRecentPassagesResponse = {
  saved_passages: [],
  recent_activity: DEFAULT_RECENT_ACTIVITY,
};

const DEFAULT_VERSE: DashboardVerseOfDayResponse = {
  verse_id: '',
  text: '',
  reference: '',
  version: 'KJV',
  locale: 'eng',
  display_date: '',
};

const STAT_CARD_META: Record<string, { icon: string; labelKey: string }> = {
  days_active: {
    icon: 'fa-solid fa-calendar-day',
    labelKey: 'dashboard.daysActive',
  },
  reading_streak: {
    icon: 'fa-solid fa-fire',
    labelKey: 'dashboard.dayStreak',
  },
  chapters_read: {
    icon: 'fa-solid fa-book',
    labelKey: 'dashboard.chaptersRead',
  },
  verses_highlighted: {
    icon: 'fa-solid fa-highlighter',
    labelKey: 'dashboard.versesHighlighted',
  },
};

@Injectable({ providedIn: 'root' })
export class DashboardFacadeService {
  private readonly api = inject(DashboardApiService);
  private readonly logger = inject(LoggerService);
  private readonly storage = inject(StorageService);

  private readonly user$ = defer(() =>
    of(
      this.storage.getItem<User>('user', 'local') ??
        this.storage.getItem<User>('user', 'session')
    )
  );

  readonly initialState: DashboardState = {
    status: 'loading',
    viewModel: this.buildViewModel(
      null,
      DEFAULT_OVERVIEW,
      DEFAULT_STATS,
      DEFAULT_RECENT,
      DEFAULT_VERSE
    ),
  };

  readonly state$ = combineLatest([
    this.user$,
    this.fetchOverview(),
    this.fetchStats(),
    this.fetchRecentPassages(),
    this.fetchVerseOfDay(),
  ]).pipe(
    map(([user, overview, stats, recent, verse]) => {
      const errors = [
        overview.error,
        stats.error,
        recent.error,
        verse.error,
      ].filter((value): value is string => value !== null);

      return {
        status: errors.length ? 'error' : 'ready',
        message: errors.length ? 'dashboard.loadError' : undefined,
        viewModel: this.buildViewModel(
          user,
          overview.data,
          stats.data,
          recent.data,
          verse.data
        ),
      } satisfies DashboardState;
    }),
    startWith(this.initialState),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly readingStreakCount$ = this.state$.pipe(
    map((state) => state.viewModel.readingStreak.current_days),
    distinctUntilChanged()
  );

  private fetchOverview() {
    return this.api.getOverview().pipe(
      retry(RETRY_COUNT),
      map((response) => ({
        data: response.data ?? DEFAULT_OVERVIEW,
        error: null,
      })),
      catchError((error) => {
        this.logger.error('Failed to load dashboard overview', error);
        return of({ data: DEFAULT_OVERVIEW, error: 'overview' });
      })
    );
  }

  private fetchStats() {
    return this.api.getStats().pipe(
      retry(RETRY_COUNT),
      map((response) => ({
        data: response.data ?? DEFAULT_STATS,
        error: null,
      })),
      catchError((error) => {
        this.logger.error('Failed to load dashboard stats', error);
        return of({ data: DEFAULT_STATS, error: 'stats' });
      })
    );
  }

  private fetchRecentPassages() {
    return this.api.getRecentPassages().pipe(
      retry(RETRY_COUNT),
      map((response) => ({
        data: response.data ?? DEFAULT_RECENT,
        error: null,
      })),
      catchError((error) => {
        this.logger.error('Failed to load dashboard recent passages', error);
        return of({ data: DEFAULT_RECENT, error: 'recent-passages' });
      })
    );
  }

  private fetchVerseOfDay() {
    return this.api.getVerseOfTheDay().pipe(
      retry(RETRY_COUNT),
      map((response) => ({
        data: response.data ?? DEFAULT_VERSE,
        error: null,
      })),
      catchError((error) => {
        this.logger.error('Failed to load dashboard verse of the day', error);
        return of({ data: DEFAULT_VERSE, error: 'verse-of-the-day' });
      })
    );
  }

  private buildViewModel(
    user: { name?: string } | null,
    overview: DashboardOverviewResponse,
    stats: DashboardStatsResponse,
    recent: DashboardRecentPassagesResponse,
    verse: DashboardVerseOfDayResponse
  ): DashboardViewModel {
    const name = user?.name ?? 'Friend';
    const greeting = this.resolveGreeting();
    const statsCards = this.mapStatCards(stats.stat_cards ?? []);

    return {
      userName: name.split(' ')[0] || name,
      greeting,
      subscription: overview.subscription ?? DEFAULT_SUBSCRIPTION,
      statsCards,
      quickAccessItems: overview.quick_access ?? [],
      continueReading: overview.continue_reading ?? [],
      readingStreak: overview.reading_streak ?? DEFAULT_READING_STREAK,
      studyStats: overview.study_stats ?? DEFAULT_STUDY_STATS,
      themeProgress: overview.theme_progress ?? DEFAULT_THEME_PROGRESS,
      aiInsight: overview.ai_insight ?? DEFAULT_AI_INSIGHT,
      verseOfTheDay: verse,
      readingProgress: stats.progress ?? DEFAULT_PROGRESS,
      readingChart: this.normalizeChartData(stats.chart),
      recentActivity: recent.recent_activity ?? DEFAULT_RECENT_ACTIVITY,
      savedPassages: recent.saved_passages ?? [],
    };
  }

  private mapStatCards(cards: StatCard[]): StatCardView[] {
    return cards.map((card) => {
      const meta = STAT_CARD_META[card.key] ?? {
        icon: 'fa-solid fa-chart-line',
        labelKey: card.key,
      };
      return {
        icon: meta.icon,
        label: meta.labelKey,
        value: card.value,
      };
    });
  }

  private normalizeChartData(chart?: ReadingChartData): ReadingChartData {
    if (!chart) {
      return {
        labels: DEFAULT_CHART_LABELS,
        values: DEFAULT_CHART_LABELS.map(() => 0),
      };
    }

    const labels = chart.labels?.length ? chart.labels : DEFAULT_CHART_LABELS;
    const values = chart.values?.length
      ? chart.values
      : DEFAULT_CHART_LABELS.map(() => 0);

    return { labels, values };
  }

  private resolveGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'dashboard.goodMorning';
    if (hour < 18) return 'dashboard.goodAfternoon';
    return 'dashboard.goodEvening';
  }
}
